<template>
  <div class="lead-form" :class="variant === 'hero' ? 'lead-form--hero' : 'lead-form--section'">
    <!-- Estado: sucesso (PDF §6 "Confirmação": sem redirecionamento brusco) -->
    <div v-if="estado === 'sucesso'" class="lead-sucesso text-center">
      <h3>Recebemos seu contato!</h3>
      <p>Nosso time comercial entrará em contato com você.</p>
      <p class="mt-2 mb-1">Prefere falar agora?</p>
      <a
        :href="telHref"
        class="btn btn-success"
        style="border-radius: 8px"
        @click="onLigarClick"
      >
        <i class="bi bi-telephone-fill"></i> Ligar {{ telefoneDisplay }}
      </a>
    </div>

    <!-- Estado: formulário -->
    <form v-else @submit.prevent="salvar" novalidate>
      <p v-if="titulo" class="lead-form__titulo">{{ titulo }}</p>
      <p v-if="subtitulo" class="lead-form__subtitulo">{{ subtitulo }}</p>

      <!-- Honeypot invisível (PDF §6 Antispam). -->
      <input
        v-model="hp"
        class="hp-field"
        type="text"
        name="empresa_site"
        tabindex="-1"
        autocomplete="off"
        aria-hidden="true"
      />

      <div class="form-group">
        <input
          v-model="form.nome"
          type="text"
          class="form-control"
          placeholder="Nome completo *"
          :class="{ 'is-invalid': erros.nome }"
          @focus="onStart"
        />
        <div v-if="erros.nome" class="invalid-feedback">{{ erros.nome }}</div>
      </div>

      <div class="form-group mt-3">
        <input
          v-model="form.empresa"
          type="text"
          class="form-control"
          placeholder="Empresa *"
          :class="{ 'is-invalid': erros.empresa }"
        />
        <div v-if="erros.empresa" class="invalid-feedback">{{ erros.empresa }}</div>
      </div>

      <div class="form-group mt-3">
        <input
          v-model="form.whatsapp"
          type="tel"
          class="form-control"
          placeholder="Telefone / WhatsApp *"
          :class="{ 'is-invalid': erros.whatsapp }"
        />
        <div v-if="erros.whatsapp" class="invalid-feedback">{{ erros.whatsapp }}</div>
      </div>

      <div class="form-group mt-3">
        <input
          v-model="form.email"
          type="email"
          class="form-control"
          placeholder="E-mail"
          :class="{ 'is-invalid': erros.email }"
        />
        <div v-if="erros.email" class="invalid-feedback">{{ erros.email }}</div>
      </div>

      <div class="form-group mt-3">
        <select
          v-model="form.interesse"
          class="form-select"
          @change="onInteresse"
        >
          <option value="">Selecione uma solução (opcional)</option>
          <option v-for="s in solucoes" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>

      <div class="form-check mt-3">
        <input
          :id="'privacidade-' + variant"
          v-model="form.privacidade"
          class="form-check-input"
          type="checkbox"
          :class="{ 'is-invalid': erros.privacidade }"
        />
        <label class="form-check-label" :for="'privacidade-' + variant" style="padding-left: 5px">
          Li e aceito a
          <a href="/politica-privacidade" target="_blank" rel="noopener">política de privacidade</a>. *
        </label>
        <div v-if="erros.privacidade" class="invalid-feedback d-block">{{ erros.privacidade }}</div>
      </div>

      <div v-if="estado === 'erro'" class="alert alert-danger mt-3 mb-0" role="alert">
        <strong>Não foi possível enviar sua solicitação.</strong>
        Tente novamente em instantes.
      </div>

      <div class="mt-4">
        <button
          type="submit"
          class="btn btn-danger w-100"
          style="border-radius: 8px"
          :disabled="estado === 'enviando'"
        >
          <span v-if="estado === 'enviando'">Enviando...</span>
          <span v-else>Enviar para o Comercial</span>
        </button>
      </div>

      <p class="lead-form__seguro">
        <i class="bi bi-lock"></i> Seus dados estão seguros conosco.
      </p>
    </form>
  </div>
</template>

<script>
import { enviarLead, validarLead } from '../services/commercialLeadService';
import { analytics } from '../services/analyticsService';
import { TELEFONE_DISPLAY, telUrl } from '../services/contato';

export default {
  name: 'LeadForm',
  props: {
    origem: { type: String, default: 'website_home' },
    variant: { type: String, default: 'section' }, // 'hero' | 'section'
    titulo: { type: String, default: '' },
    subtitulo: { type: String, default: '' },
  },
  data() {
    return {
      estado: 'inicial', // inicial | enviando | sucesso | erro
      hp: '',
      startEnviado: false,
      solucoes: [
        'Garantia de Aluguel',
        'Boleto Garantido',
        'Análise e Cobrança de Cheque',
        'Maquininha de Cartão de Crédito',
        'Cheque Garantido',
        'Análise e Cobrança de Boleto',
        'Plataforma de Crédito',
      ],
      form: {
        nome: '',
        empresa: '',
        whatsapp: '',
        email: '',
        interesse: '',
        privacidade: false,
      },
      erros: {},
    };
  },
  computed: {
    telHref() {
      return telUrl;
    },
    telefoneDisplay() {
      return TELEFONE_DISPLAY;
    },
  },
  methods: {
    onStart() {
      if (this.startEnviado) return;
      this.startEnviado = true;
      analytics.leadFormStart(this.origem);
    },
    onInteresse() {
      if (this.form.interesse) analytics.interesseSolucaoClick(this.form.interesse);
    },
    onLigarClick() {
      analytics.supportClick(this.origem);
    },
    async salvar() {
      if (this.estado === 'enviando') return;

      // Honeypot preenchido = bot: finge sucesso e não envia (PDF §6 Antispam).
      if (this.hp) {
        this.estado = 'sucesso';
        return;
      }

      const { valido, erros } = validarLead(this.form);
      this.erros = erros;
      if (!valido) return;

      this.estado = 'enviando';
      try {
        await enviarLead(this.form, this.origem);
        analytics.leadFormSubmitSuccess(this.origem, this.form.interesse);
        this.estado = 'sucesso';
      } catch (e) {
        analytics.leadFormSubmitError(this.origem, 'network');
        this.estado = 'erro';
      }
    },
  },
};
</script>

<style scoped>
.hp-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.lead-form__titulo {
  font-size: 20px;
  font-weight: 700;
  color: #191919;
  margin: 0 0 4px;
}
.lead-form__subtitulo {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px;
}
.lead-form__seguro {
  font-size: 12px;
  color: #8a8a8a;
  text-align: center;
  margin: 12px 0 0;
}

.btn-danger {
  background: #bf1b39;
  border-color: #bf1b39;
}

.lead-sucesso h3 {
  color: #bf1b39;
  font-weight: 700;
}

/* Variante Hero: card branco compacto sobre o banner. */
.lead-form--hero {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
}

/* Variante Seção: card dentro do bloco de captação. */
.lead-form--section {
  background: #fff;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.06);
}

@media (max-width: 767px) {
  .lead-form--hero,
  .lead-form--section {
    padding: 20px;
  }
}
</style>
